import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import { StatusBadge } from "@/components/Bits";
import api, { COUNTRY_NAMES, fmt, fmtTime, STATUS_LABELS } from "@/lib/api";
import { toast } from "sonner";
import { Pause, Play, XCircle, ArrowLeft, Copy, Pencil, Undo2, Zap } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

const RAW_STATUS_LABELS = {
  sent: "Envoyé au serveur WhatsApp",
  delivered: "Livré sur le téléphone",
  read: "Lu par le destinataire",
  failed: "Non livré",
  noAccount: "Pas de compte WhatsApp",
  notInGroup: "Retiré du groupe",
};

export default function CampaignMonitor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get(`/campaigns/${id}/monitor`);
    setData(data);
  }, [id]);

  useEffect(() => {
    load();
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, [load]);

  const action = async (verb, label) => {
    await api.post(`/campaigns/${id}/${verb}`);
    toast.success(label);
    load();
  };

  const c = data?.campaign;
  const counts = data?.counts || {};

  return (
    <Layout
      title={c?.name || "Campagne"}
      subtitle="Suivi temps réel de la diffusion"
      actions={
        <div className="flex gap-2">
          <button onClick={() => navigate("/campagnes")} data-testid="back-campaigns-btn"
            className="rounded-lg border border-gray-200 bg-white p-2.5 hover:bg-gray-50">
            <ArrowLeft className="h-4 w-4 text-gray-600" />
          </button>
          {c?.status === "running" && (
            <button onClick={() => action("send-now", "Messages replanifiés immédiatement")} data-testid="send-now-btn"
              className="flex items-center gap-1.5 rounded-lg bg-emerald-500 px-3 py-2.5 text-sm text-white hover:bg-emerald-600">
              <Zap className="h-4 w-4" /> Envoyer maintenant
            </button>
          )}
          {c?.status === "running" && (
            <button onClick={() => action("pause", "Campagne mise en pause")} data-testid="pause-campaign-btn"
              className="flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm hover:bg-gray-50">
              <Pause className="h-4 w-4" /> Pause
            </button>
          )}
          {c?.status === "paused" && (
            <button onClick={() => action("resume", "Campagne reprise")} data-testid="resume-campaign-btn"
              className="flex items-center gap-1.5 rounded-lg bg-emerald-500 px-3 py-2.5 text-sm text-white hover:bg-emerald-600">
              <Play className="h-4 w-4" /> Reprendre
            </button>
          )}
          {["running", "paused", "scheduled"].includes(c?.status) && (
            <button onClick={() => action("cancel", "Campagne annulée")} data-testid="cancel-campaign-btn"
              className="flex items-center gap-1.5 rounded-lg border border-red-200 bg-white px-3 py-2.5 text-sm text-red-600 hover:bg-red-50">
              <XCircle className="h-4 w-4" /> Annuler
            </button>
          )}
          {["draft", "scheduled"].includes(c?.status) && (
            <button onClick={() => navigate(`/campagnes/${id}/modifier`)} data-testid="edit-campaign-btn"
              className="flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm hover:bg-gray-50">
              <Pencil className="h-4 w-4" /> Modifier
            </button>
          )}
          {c && !["draft", "running"].includes(c.status) && (
            <button
              onClick={async () => {
                try {
                  await api.post(`/campaigns/${id}/reopen`);
                  toast.success("Campagne remise en brouillon");
                  navigate(`/campagnes/${id}/modifier`);
                } catch (e) {
                  const d = e?.response?.data?.detail;
                  toast.error(typeof d === "string" ? d : "Impossible de rouvrir");
                }
              }}
              data-testid="reopen-campaign-btn"
              className="flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm hover:bg-gray-50">
              <Undo2 className="h-4 w-4" /> Rouvrir
            </button>
          )}
          <button
            onClick={async () => {
              try {
                const { data } = await api.post(`/campaigns/${id}/duplicate`);
                toast.success("Copie créée en brouillon");
                navigate(`/campagnes/${data.id}/modifier`);
              } catch {
                toast.error("Duplication impossible");
              }
            }}
            data-testid="duplicate-campaign-btn"
            className="flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm hover:bg-gray-50">
            <Copy className="h-4 w-4" /> Dupliquer
          </button>
        </div>
      }
    >
      {!data ? <p className="text-sm text-gray-500">Chargement…</p> : (
        <div className="space-y-5" data-testid="campaign-monitor-page">
          <div className="flex items-center gap-3">
            <StatusBadge status={c.status} testId="campaign-status-badge" />
            <span className="text-xs text-gray-500">
              {(c.target_countries || []).map((x) => COUNTRY_NAMES[x] || x).join(", ") || "Tous pays"} ·
              fenêtre {c.send_window_start}–{c.send_window_end} · étalement {c.spread_duration_hours} h
            </span>
          </div>

          {data.blocked_info && (
            <div className="rounded-lg border border-amber-300 bg-amber-50 p-4" data-testid="blocked-alert">
              <p className="text-sm font-medium text-amber-900">
                {fmt(data.blocked_info.count)} message(s) bloqué(s) — {data.blocked_info.reason}
              </p>
              <p className="mt-1 text-xs text-amber-800">
                Pays concerné(s) : {(data.blocked_info.countries || []).map((x) => COUNTRY_NAMES[x] || x).join(", ")} ·{" "}
                {data.blocked_info.instances_available > 0
                  ? `${data.blocked_info.instances_available} instance(s) disponible(s) — reprise possible dès maintenant`
                  : "Aucune instance disponible : la reprise sera automatique dès qu'une instance sera reconnectée (vérification toutes les 5 min)"}
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                <button
                  onClick={async () => {
                    try {
                      const { data: r } = await api.post(`/campaigns/${id}/requeue-blocked`);
                      toast.success(`${r.requeued} message(s) relancé(s)`);
                      load();
                    } catch (e) {
                      const d = e?.response?.data?.detail;
                      toast.error(typeof d === "string" ? d : "Relance impossible");
                    }
                  }}
                  data-testid="requeue-blocked-btn"
                  className="rounded-lg bg-amber-600 px-3 py-2 text-xs font-medium text-white hover:bg-amber-700">
                  Relancer les bloqués
                </button>
                {c.status === "running" && (
                  <button onClick={() => action("pause", "Campagne mise en pause")} data-testid="pause-blocked-campaign-btn"
                    className="rounded-lg border border-amber-300 bg-white px-3 py-2 text-xs font-medium text-amber-900 hover:bg-amber-100">
                    Mettre la campagne en pause
                  </button>
                )}
              </div>
              {c.status === "paused" && (
                <p className="mt-2 text-xs text-amber-800">
                  Campagne en pause : aucune reprise automatique tant que vous ne cliquez pas sur « Reprendre ».
                </p>
              )}
            </div>
          )}

          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
            {[
              ["En attente", counts.pending || 0, "text-gray-900", "monitor-pending"],
              ["Envoyés", counts.sent || 0, "text-emerald-700", "monitor-sent"],
              ["Livrés", (data.delivery?.delivered || 0) + (data.delivery?.read || 0), "text-emerald-600", "monitor-delivered"],
              ["Lus", data.delivery?.read || 0, "text-sky-700", "monitor-read"],
              ["Échecs d'envoi", counts.failed || 0, "text-red-600", "monitor-failed"],
            ].map(([label, value, color, tid]) => (
              <div key={label} className="card-tactical p-4" data-testid={tid}>
                <p className="text-sm text-gray-500">{label}</p>
                <p className={`mono text-3xl font-semibold mt-2 ${color}`}>{fmt(value)}</p>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              ["Ignorés", counts.skipped || 0, "monitor-skipped"],
              ["Bloqués", counts.blocked || 0, "monitor-blocked"],
              ["Planifiée pour", c.scheduled_for ? fmtTime(c.scheduled_for) : "—", "monitor-scheduled-for"],
            ].map(([label, value, tid]) => (
              <div key={label} className="card-tactical p-4 flex items-center justify-between" data-testid={tid}>
                <p className="text-sm text-gray-500">{label}</p>
                <p className="mono text-sm font-medium text-gray-900">{typeof value === "number" ? fmt(value) : value}</p>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="card-tactical p-5 lg:col-span-2" data-testid="timeline-chart">
              <h2 className="text-base font-semibold text-gray-900 mb-4">Chronologie des envois</h2>
              {data.timeline.length === 0 ? (
                <p className="text-sm text-gray-500">Aucun envoi enregistré.</p>
              ) : (
                <ResponsiveContainer width="100%" height={240}>
                  <LineChart data={data.timeline.map((t) => ({ ...t, hour: t.hour?.slice(11) + "h" }))}>
                    <CartesianGrid stroke="#F0F1EF" vertical={false} />
                    <XAxis dataKey="hour" tick={{ fontSize: 11, fill: "#6B7280" }} />
                    <YAxis tick={{ fontSize: 11, fill: "#6B7280" }} allowDecimals={false} />
                    <Tooltip />
                    <Line type="monotone" dataKey="sent" stroke="#10B981" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>

            <div className="card-tactical p-5" data-testid="per-instance-breakdown">
              <h2 className="text-base font-semibold text-gray-900 mb-4">Par instance</h2>
              <div className="space-y-3">
                {data.per_instance.length === 0 && <p className="text-sm text-gray-500">—</p>}
                {data.per_instance.map((i, idx) => (
                  <div key={idx} className="rounded-lg border border-gray-200 p-3">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium text-gray-900">{i.label}</p>
                      <span className="text-xs text-gray-500">{COUNTRY_NAMES[i.country] || i.country}</span>
                    </div>
                    <div className="mt-2 grid grid-cols-3 gap-2 text-xs">
                      <span className="mono text-emerald-700">{i.sent} env.</span>
                      <span className="mono text-gray-600">{i.pending} att.</span>
                      <span className="mono text-red-600">{i.failed} éch.</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="card-tactical p-5" data-testid="next-sends">
              <h2 className="text-base font-semibold text-gray-900 mb-3">Prochains envois planifiés</h2>
              <ul className="space-y-2">
                {data.next_sends.length === 0 && <li className="text-sm text-gray-500">Aucun envoi en attente.</li>}
                {data.next_sends.map((n) => (
                  <li key={n.id} className="flex items-center justify-between text-sm border-b border-gray-100 pb-2">
                    <span className="mono text-gray-900">{n.phone}</span>
                    <span className="mono text-xs text-gray-500">{fmtTime(n.scheduled_at)}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="card-tactical p-5" data-testid="send-logs">
              <h2 className="text-base font-semibold text-gray-900 mb-3">Journal d'envoi</h2>
              <ul className="space-y-2 max-h-72 overflow-y-auto">
                {data.logs.length === 0 && <li className="text-sm text-gray-500">Aucun log.</li>}
                {data.logs.map((l) => (
                  <li key={l.id} className="flex items-center justify-between gap-2 text-sm border-b border-gray-100 pb-2">
                    <div className="min-w-0">
                      <p className="mono text-xs text-gray-900">{l.phone}</p>
                      <p className="text-[11px] text-gray-500">{l.instance_label} · {fmtTime(l.at)} · HTTP {l.http_status}</p>
                    </div>
                    <StatusBadge status={l.ok ? "sent" : "failed"} />
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="card-tactical p-5" data-testid="delivery-table">
            <h2 className="text-base font-semibold text-gray-900 mb-3">Accusés de réception</h2>
            <p className="text-xs text-gray-500 mb-3">
              Information poussée par GreenAPI (<span className="mono">outgoingMessageStatus</span>) :
              nous n'interrogeons jamais WhatsApp, ce sont les accusés que WhatsApp renvoie de lui-même.
            </p>
            <div className="flex flex-wrap gap-3">
              {[
                ["Envoyés (acceptés par GreenAPI)", counts.sent || 0, "text-gray-900"],
                ["Livré", data.delivery?.delivered || 0, "text-emerald-600"],
                ["Lu", data.delivery?.read || 0, "text-sky-700"],
                ["Non livré", data.delivery?.failed || 0, "text-red-600"],
              ].map(([l, v, color]) => (
                <div key={l} className="rounded-lg border border-gray-200 px-4 py-2.5">
                  <p className="text-xs text-gray-500">{l}</p>
                  <p className={`mono text-lg font-semibold ${color}`}>{fmt(v)}</p>
                </div>
              ))}
            </div>
            {Object.keys(data.delivery_raw || {}).length > 0 && (
              <div className="mt-4 border-t border-gray-100 pt-3" data-testid="delivery-raw-detail">
                <p className="text-xs font-medium text-gray-700 mb-2">Détail des statuts GreenAPI</p>
                <ul className="space-y-1">
                  {Object.entries(data.delivery_raw).map(([raw, n]) => (
                    <li key={raw} className="flex items-center justify-between text-xs">
                      <span className="text-gray-600">{RAW_STATUS_LABELS[raw] || raw}</span>
                      <span className="mono font-medium text-gray-900">{fmt(n)}</span>
                    </li>
                  ))}
                </ul>
                {data.delivery_raw.noAccount > 0 && (
                  <p className="mt-2 text-xs text-amber-800">
                    « Pas de compte WhatsApp » signifie que le numéro n'est pas inscrit sur WhatsApp :
                    le message ne peut pas être livré, ce n'est pas une erreur d'envoi.
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </Layout>
  );
}
